import CircularProgress from "@material-ui/core/CircularProgress";
import _ from "lodash";
import React, {useEffect, useMemo, useState} from "react";
import {toast} from "react-toastify";
import Auflagen from "../../components/betriebsakte/bescheid/Auflagen";
import Bescheidsdaten from "../../components/betriebsakte/bescheid/Bescheidsdaten";
import EABetriebsakteDatenContainer from "../../components/betriebsakte/EABetriebsakteDatenContainer";
import EABetriebsakteMainContainer from "../../components/betriebsakte/EABetriebsakteMainContainer";
import {confirmDeleteOption} from "../../components/ConfirmationDialog/confirmationDialogOptions";
import {useConfirmation} from "../../components/ConfirmationDialog/ConfirmationDialogProvider";
import EAButtonBar from "../../components/EAButtonBar";
import EATabs from "../../components/EATabs";
import {
    BESCHEID_ANGELEGT,
    BESCHEID_BEANTRAGT,
    BESCHEID_FREIGEGEBEN,
    BESCHEID_IN_BEARBEITUNG,
    bescheidstatusIdToObj,
} from "../../consts/bescheidsstatus";
import {BEAUFTRAGTER, BETREIBER, FACHADMIN, SYSTEMADMIN, ZUST_SBB} from "../../consts/funktionaleRollen";
import routes from "../../consts/routes.json";
import {
    createBescheidAntragSchema,
    createBescheidSchema,
    createFreigegebenerBescheidSchema,
    createInitialBescheid,
} from "../../initialObjects/BescheidObjects";
import {
    deleteBescheidEndpoint,
    deleteBescheidsAntragEndpoint,
    getBescheidEndpoint,
    getSelectableVorbescheideEndpoint,
    postBescheidEndpoint,
    postBescheidsantragEndpoint,
    putBescheidEndpoint,
    putBescheidsantragEndpoint,
} from "../../services/BescheidsService";
import {getBehoerdeEndpoint, getBescheidQualitaetEndpoint, getFilteredBescheidArtenEndpoint,} from "../../services/WertelistenService";
import {handleValidatedApiException, useHttp} from "../../services/HttpService";
import {logger} from "../../services/LogService";
import {useForm} from "../../utils/FormUtils";
import {changeFieldValueIfPresent} from "../../utils/ObjectUtils";
import {userHasAnyRole} from "../../utils/SecurityUtils";
import {NUMBER, STRING} from "../../utils/URLUtils";
import usePathParam from "../../utils/usePathParam";
import {useHttpSortById} from "../../utils/usePreformattedHttp";
import useQueryParam from "../../utils/useQueryParam";

const antragsStatusOptions = [BESCHEID_ANGELEGT, BESCHEID_BEANTRAGT].map((o) => bescheidstatusIdToObj(o));
const beantragtStatusOptions = [BESCHEID_BEANTRAGT, BESCHEID_IN_BEARBEITUNG, BESCHEID_FREIGEGEBEN].map((o) => bescheidstatusIdToObj(o));
const bescheidStatusOptions = [BESCHEID_IN_BEARBEITUNG, BESCHEID_FREIGEGEBEN].map((o) => bescheidstatusIdToObj(o));

function BescheidsdatenView({modus, betriebsId, reloadNavigation, setToggleAllowed, location, history}) {
    const tabBescheid = "bescheid";
    const tabAuflagen = "auflagen";
    const bescheidsId = usePathParam("bescheidsid", NUMBER);
    const preselectedBescheidQualitaetId = useQueryParam("qualitaet", STRING);
    const readonly = !bescheidsId ? false : modus === "l";
    const confirm = useConfirmation();
    const {
        values,
        setValues,
        errors,
        setErrors,
        touchedValues,
        setTouchedValues,
        setSchema,
        formFactory,
        submit,
        submitLoading,
    } = useForm({
        readonly: () => readonly || userHasAnyRole([BETREIBER, BEAUFTRAGTER])
    });

    const [selectedTab, setSelectedTab] = useState(tabBescheid);
    const [statusOptionen, setStatusOptionen] = useState([]);
    const [behoerdeOptionen] = useHttpSortById(getBehoerdeEndpoint);
    const [allBescheidQualitaeten] = useHttp(getBescheidQualitaetEndpoint);
    const [bescheidLoader] = useHttp(getBescheidEndpoint(bescheidsId), {manual: !bescheidsId});
    const [originalStatus, setOriginalStatus] = useState();
    const [bescheidQualitaet, setBescheidQualitaet] = useState();
    const [isVorbescheidSelectable, setIsVorbescheidSelectable] = useState(false);
    const [vorbescheidOptionen, loadVorbescheide] = useHttp(getSelectableVorbescheideEndpoint(betriebsId, bescheidsId, bescheidQualitaet?.id), {manual: true});
    const [bescheidArtOptionen, loadBescheidArten] = useHttp(getFilteredBescheidArtenEndpoint(bescheidQualitaet?.id), {manual: true});

    const isFreigegeben = useMemo(() => !originalStatus || originalStatus?.id === BESCHEID_FREIGEGEBEN, [originalStatus]);

    useEffect(() => {
        if (bescheidQualitaet) {
            loadVorbescheide().then();
            loadBescheidArten().then();
        }
    }, [bescheidQualitaet, loadVorbescheide, loadBescheidArten]);

    useEffect(() => {
        if (preselectedBescheidQualitaetId && !allBescheidQualitaeten.loading) {
            const preselectedBescheidQualitaet = allBescheidQualitaeten.data.find(item => item.id === preselectedBescheidQualitaetId);
            setBescheidQualitaet(preselectedBescheidQualitaet);
        }
    }, [preselectedBescheidQualitaetId, allBescheidQualitaeten, setBescheidQualitaet]);

    useEffect(() => {
        if (bescheidsId === undefined) {
            return;
        }

        setErrors({})

        if (!bescheidsId) {
            const initialBescheid = createInitialBescheid(betriebsId, bescheidQualitaet);
            setValues(initialBescheid);
            setOriginalStatus(_.clone(initialBescheid.bescheidsstatus));
            setIsVorbescheidSelectable(bescheidQualitaet?.id !== "ERKLAERUNG");
        } else if (bescheidLoader.data) {
            logger.debug("Geladener Bescheid", bescheidLoader.data);
            setOriginalStatus(_.clone(bescheidLoader.data.bescheidsstatus));
            setValues(bescheidLoader.data);
            setIsVorbescheidSelectable(bescheidLoader.data.bescheidQualitaet?.id !== "ERKLAERUNG");
            setBescheidQualitaet(bescheidLoader.data.bescheidQualitaet);
        }


    }, [bescheidsId, betriebsId, setErrors, setValues, bescheidLoader.data, bescheidQualitaet, setBescheidQualitaet, setIsVorbescheidSelectable, allBescheidQualitaeten]);

    useEffect(() => {
        if (userHasAnyRole([FACHADMIN, SYSTEMADMIN])) {
            setToggleAllowed(true);
        } else if (
            userHasAnyRole([ZUST_SBB]) &&
            (!originalStatus || originalStatus?.id === BESCHEID_BEANTRAGT || originalStatus?.id === BESCHEID_IN_BEARBEITUNG || originalStatus?.id === BESCHEID_FREIGEGEBEN)
        ) {
            setToggleAllowed(true);
        } else if (userHasAnyRole([BETREIBER, BEAUFTRAGTER]) && (originalStatus === null || originalStatus?.id === BESCHEID_ANGELEGT)) {
            setToggleAllowed(true);
        } else {
            setToggleAllowed(false);
        }
    }, [originalStatus, setToggleAllowed]);

    useEffect(() => {
        if (values.bescheidsstatus?.id === BESCHEID_FREIGEGEBEN) {
            setSchema(createFreigegebenerBescheidSchema());
        } else if (values.bescheidsstatus?.id === BESCHEID_ANGELEGT || values.bescheidsstatus?.id === BESCHEID_BEANTRAGT) {
            setSchema(createBescheidAntragSchema());
        } else {
            setSchema(createBescheidSchema());
        }
    }, [values.bescheidsstatus, originalStatus, setSchema]);

    useEffect(() => {
        if (!originalStatus) {
            return;
        }
        if (originalStatus.id === BESCHEID_ANGELEGT) {
            setStatusOptionen(antragsStatusOptions);
        } else if (originalStatus.id === BESCHEID_BEANTRAGT) {
            setStatusOptionen(beantragtStatusOptions);
        } else {
            setStatusOptionen(bescheidStatusOptions);
        }
    }, [originalStatus, setStatusOptionen]);

    const prepareForUpload = (bescheid) => {
        changeFieldValueIfPresent(bescheid, "betrieb", "id");
        changeFieldValueIfPresent(bescheid, "behoerde", "id");
        changeFieldValueIfPresent(bescheid, "bescheidsstatus", "id");
        changeFieldValueIfPresent(bescheid, "bescheidart", "id");
        changeFieldValueIfPresent(bescheid, "bescheidQualitaet", "id");
        changeFieldValueIfPresent(bescheid, "vorbescheid", "id");
        if (bescheid.messstellen) {
            bescheid.messstellen = bescheid.messstellen.map((m) => m.id);
        }

        if (bescheid.auflagen) {
            bescheid.auflagen = bescheid.auflagen.map((auflage) => {
                auflage.art = auflage.art.id;
                auflage.abwasseranlage = auflage.abwasseranlage.id;
                return auflage;
            });
        }
        return bescheid;
    };

    function toLeseModus(bescheidId) {
        if (
            location.pathname.indexOf(`bescheid/${bescheidId}/bescheidsdaten`) > -1 ||
            location.pathname.indexOf(`antrag/${bescheidId}/bescheidsdaten`) > -1
        ) {
            const hasModus = location.search.indexOf("modus") > -1;
            if (modus === "s") {
                history.push(location.pathname + (hasModus ? _.replace(location.search, "modus=s", "modus=l") : "?modus=l"));
            } else {
                history.push(location.pathname + (hasModus ? _.replace(location.search, "modus=l", "modus=s") : "?modus=s"));
            }
        } else {
            history.replace(location.pathname.replace("neu", bescheidId) + "?modus=l");
        }
    }

    function setDataFromSubmit(data, isAntrag, isUpdated) {
        toast.success((isAntrag ? "Bescheidsantrag" : "Bescheid") + " erfolgreich " + (isUpdated ? "aktualisiert" : "erstellt"));
        setValues(data);
        setOriginalStatus(_.clone(data?.bescheidsstatus));
        if (data?.bescheidsstatus.id === BESCHEID_FREIGEGEBEN) {
            toLeseModus(data.id);
        }
    }

    const saveAsAdmin = () => {
        if (bescheidsId && (values?.bescheidsstatus?.id === BESCHEID_ANGELEGT || values?.bescheidsstatus?.id === BESCHEID_BEANTRAGT)) {
            submit(prepareForUpload, putBescheidsantragEndpoint(bescheidsId), "PUT")
                .then(({data}) => {
                    if (
                        touchedValues.bescheidsstatus ||
                        touchedValues.bescheidsdatum ||
                        touchedValues.aktenzeichen ||
                        touchedValues.messstellen
                    ) {
                        reloadNavigation();
                    }
                    setDataFromSubmit(data, true, true);
                })
                .catch((e) => handleValidatedApiException(e, "Error updating Bescheidsantrag"));
        } else if (bescheidsId) {
            submit(prepareForUpload, putBescheidEndpoint(bescheidsId), "PUT")
                .then(({data}) => {
                    if (
                        touchedValues.bescheidsstatus ||
                        touchedValues.bescheidsdatum ||
                        touchedValues.aktenzeichen ||
                        touchedValues.messstellen
                    ) {
                        reloadNavigation();
                    }
                    setDataFromSubmit(data, false, true);
                })
                .catch((e) => {
                    if (e?.response?.status === 400 && e?.response?.data?.validationErrors) {
                        setErrors({...e?.response?.data?.validationErrors});
                        e.isValidationHandled = true;
                    } else if (e?.response?.status === 400 && e?.response?.data) {
                        toast.warn(e?.response?.data);
                        setErrors({bescheidsstatus: e?.response?.data?.error});
                        e.isValidationHandled = true;
                    }
                    return handleValidatedApiException(e, "Error updating Bescheid");
                });
        } else {
            submit(prepareForUpload, postBescheidEndpoint, "POST")
                .then(({data}) => {
                    reloadNavigation();
                    setDataFromSubmit(data, false, false);
                })
                .catch((e) => {
                    if (e?.response?.status === 400 && e?.response?.data?.validationErrors) {
                        setErrors({...e?.response?.data?.validationErrors});
                        e.isValidationHandled = true;
                    } else if (e?.response?.status === 400 && e?.response?.data?.error) {
                        toast.warn(e?.response?.data);
                        setErrors({bescheidsstatus: e?.response?.data?.error});
                        e.isValidationHandled = true;
                    } else {
                        toast.error("Ein unerwarteter Fehler ist aufgetreten");
                    }

                    return handleValidatedApiException(e, "Error creating Bescheid");
                });
        }
    };

    const save = () => {
        if (bescheidsId) {
            submit(prepareForUpload, putBescheidsantragEndpoint(bescheidsId), "PUT")
                .then(({data}) => {
                    if (
                        touchedValues.bescheidsstatus ||
                        touchedValues.bescheidsdatum ||
                        touchedValues.aktenzeichen ||
                        touchedValues.messstellen
                    ) {
                        reloadNavigation();
                    }
                    setDataFromSubmit(data, true, true);
                })
                .catch((e) => handleValidatedApiException(e, "Error updating Bescheidsantrag"));
        } else {
            submit(prepareForUpload, postBescheidsantragEndpoint, "POST")
                .then(({data}) => {
                    reloadNavigation();
                    setDataFromSubmit(data, true, false);
                })
                .catch((e) => handleValidatedApiException(e, "Error creating Bescheidsantrag"));
        }
    };

    const handleDeleteBescheid = () => {
        if (values?.bescheidsstatus?.id === BESCHEID_ANGELEGT || values?.bescheidsstatus?.id === BESCHEID_BEANTRAGT) {
            confirm(
                confirmDeleteOption("den Bescheid", () => {
                    submit(() => {
                    }, deleteBescheidsAntragEndpoint(bescheidsId), "DELETE", false)
                        .then(() => {
                            reloadNavigation();
                            history.replace(`${routes.BETRIEBSAKTE_RAW}/${betriebsId}`);
                        })
                        .catch((e) => {
                            if (e?.response?.status === 409) {
                                toast.error(e?.response?.data?.validationErrors?.vorbescheid);
                                return;
                            }
                            handleValidatedApiException(e, "Error while saving Bescheid", "Der Bescheid konnte nicht gelöscht werden");
                        });
                })
            );
        } else {
            confirm(
                confirmDeleteOption("den Bescheid", () => {
                    submit(() => {
                    }, deleteBescheidEndpoint(bescheidsId), "DELETE", false)
                        .then(() => {
                            reloadNavigation();
                            history.replace(`${routes.BETRIEBSAKTE_RAW}/${betriebsId}`);
                        })
                        .catch((e) => {
                            if (e?.response?.status === 409) {
                                toast.error(e?.response?.data?.validationErrors?.vorbescheid);
                                return;
                            }
                            handleValidatedApiException(e, "Error while saving Bescheid", "Der Bescheid konnte nicht gelöscht werden");
                        });
                })
            );
        }
    };

    const renderButtonBar = () => {
        return (
            <EAButtonBar
                style={{
                    marginTop: "var(--buttonBar-spacing-top)",
                    marginBottom: "var(--vertical-spacing)",
                    marginRight: "10px",
                    marginLeft: "5px",
                }}
            >
                {bescheidsId &&
                    ((userHasAnyRole([ZUST_SBB, FACHADMIN, SYSTEMADMIN]) && originalStatus?.id === BESCHEID_IN_BEARBEITUNG) ||
                        (userHasAnyRole([BETREIBER, BEAUFTRAGTER, FACHADMIN, SYSTEMADMIN, ZUST_SBB]) &&
                            (originalStatus === null ||
                                originalStatus?.id === BESCHEID_ANGELEGT ||
                                originalStatus?.id === BESCHEID_BEANTRAGT))) &&
                    formFactory.renderButton("handleDeleteBescheid", "handleDeleteBescheid", "Löschen", handleDeleteBescheid, {
                        "data-cy": "handleDeleteBescheid",
                        style: {marginRight: "auto"},
                    })}
                {userHasAnyRole([ZUST_SBB, FACHADMIN, SYSTEMADMIN]) &&
                    formFactory.renderButton("saveAsAdmin", "saveAsAdmin", "Speichern", saveAsAdmin, {
                        "data-cy": "saveZustSbd",
                        color: "primary",
                    })}
                {userHasAnyRole([BETREIBER, BEAUFTRAGTER]) &&
                    formFactory.renderButton("save", "save", "Antrag speichern", save, {
                        "data-cy": "saveBetreiber",
                        color: "primary",
                    })}
            </EAButtonBar>
        );
    };

    const buildTabs = () => {
        const options = [tabBescheid];
        options.push(tabAuflagen);
        return options;
    };

    const getTabErrors = () => {
        const tabErrors = {};
        if (errors && Object.keys(errors).length > 0) {
            for (const error in errors) {
                if (!errors.hasOwnProperty(error)) {
                    continue;
                }
                if (error.startsWith("auflagen")) {
                    if (Object.keys(errors[error]).length > 0) tabErrors[tabAuflagen] = true;
                } else {
                    tabErrors[tabBescheid] = true;
                }
            }
        }
        return tabErrors;
    };

    function handleMessstellenChange(newMessstellen) {
        const touchedValuesToSet = {...touchedValues};
        _.set(touchedValuesToSet, "messstellen", true);
        setTouchedValues(touchedValuesToSet);
        setValues((oldV) => {
            return {...oldV, messstellen: newMessstellen};
        });
    }

    if (bescheidLoader.loading || submitLoading) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <CircularProgress/>
            </div>
        );
    }
    const backgroundColor = values.history ? {background: "#FFFFFF", filter: "sepia()"} : {};
    return (
        <EABetriebsakteMainContainer style={backgroundColor}>
            <EATabs
                value={selectedTab}
                onChange={(newSelectedTab) => setSelectedTab(newSelectedTab)}
                options={buildTabs()}
                optionLabels={{[tabBescheid]: "Bescheid", [tabAuflagen]: "Auflagen"}}
                errors={getTabErrors()}
            />
            <EABetriebsakteDatenContainer>
                {selectedTab === tabBescheid && (
                    <Bescheidsdaten
                        formFactory={formFactory}
                        originalStatus={originalStatus}
                        bescheidsId={bescheidsId}
                        originalBescheidId={values?.bescheidId}
                        modus={modus}
                        freigabe={{
                            freigabeAm: values.freigabeAm,
                            freigabeVon: values.freigabeVon,
                        }}
                        messstellen={values?.messstellen}
                        setMessstellen={handleMessstellenChange}
                        behoerdeOptionen={behoerdeOptionen}
                        statusOptionen={statusOptionen}
                        erlaubnisOptionen={bescheidArtOptionen}
                        vorbescheidOptionen={vorbescheidOptionen}
                        isVorbescheidSelectable={isVorbescheidSelectable}
                        bescheidQualitaet={bescheidQualitaet}
                        isFreigegeben={isFreigegeben}
                    />
                )}
                {selectedTab === tabAuflagen && (
                    <Auflagen
                        data={values.auflagen}
                        onChange={(name, date) => {
                            const valuesToSet = {...values};
                            _.set(valuesToSet, name, date);
                            setValues(valuesToSet);
                        }}
                        betriebsId={betriebsId}
                        formFactory={formFactory}
                        modus={modus}
                        isFreigegeben={isFreigegeben}
                    />
                )}
            </EABetriebsakteDatenContainer>
            {renderButtonBar()}
        </EABetriebsakteMainContainer>
    );
}

export default BescheidsdatenView;
